// src/client/components/data-display/UserToursTable.js
import { canCancelTour, formatEmailForTable, formatMoveInDate } from "@/client/utils/tourTableHelpers.js";
import Tooltip from "@/shared/components/feedback/Tooltip.js";
import TextModal from "@/shared/components/modals/TextModal.js";
import {
	CalendarDaysIcon as CalendarDaysOutlineIcon,
	PencilSquareIcon as PencilSquareOutlineIcon,
} from "@heroicons/react/24/outline";
import {
	CalendarDaysIcon,
	CheckCircleIcon,
	EnvelopeIcon,
	PencilSquareIcon,
	TrashIcon,
	XCircleIcon,
} from "@heroicons/react/24/solid";
import { useState } from "react";

// TODO: Evaluate whether cancel flow needs adjustment for user-scoped context (currently uses propertySlug from tour data)
export default function UserToursTable({
	activeTab,
	filteredTours,
	tours,
	onCancel,
	forceMobileLayout,
	showAgent = false,
}) {
	const [messageModal, setMessageModal] = useState({ isOpen: false, message: null });

	// ==================== RENDERING HELPERS ====================
	// All tours on this page belong to the current user, so ownership checks are not needed.
	// canEditTourNotes is always true — every tour here is the agent's own.

	/**
	 * Renders attendance badge for completed tours.
	 *
	 * Only "attended" (green) and "no_show" (red) render a badge. The "unrecorded"
	 * state intentionally renders nothing.
	 *
	 * Principle: confirm decisions, don't flag their absence. Green/red badges
	 * confirm settled state — the agent has decided what happened. The unrecorded
	 * state is the absence of a decision, which is already conveyed by the row's
	 * lack of a green or red badge. Adding an orange icon for "no decision yet"
	 * is redundant noise that experienced agents tune out, and on screens with
	 * many unrecorded rows it reads as a wall of warnings rather than useful
	 * differentiation.
	 *
	 * For "show me what needs action" workflows, the Tasks page is the proper
	 * surface — it filters to unrecorded tours by definition.
	 */
	const renderAttendanceBadge = (tour) => {
		if (tour.attendance === "attended") {
			return (
				<Tooltip text="Attended">
					<span className="ml-1.5 shrink-0">
						<CheckCircleIcon className="h-4 w-4 text-green-500" />
					</span>
				</Tooltip>
			);
		}
		if (tour.attendance === "no_show") {
			return (
				<Tooltip text="No show">
					<span className="ml-1.5 shrink-0">
						<XCircleIcon className="h-4 w-4 text-red-500" />
					</span>
				</Tooltip>
			);
		}
		return null;
	};

	/**
	 * Renders a "Leased" pill for completed tours that converted to a lease.
	 *
	 * A pill rather than an icon: has_leased can only be true when attendance is
	 * "attended" (enforced in updateTourFeedback), so an icon would sit directly
	 * beside the green attendance check and the two green marks would compete.
	 * Uses the same pill styling as the lead source badge.
	 */
	const renderLeasedPill = (tour) => {
		if (!tour.has_leased) {
			return null;
		}
		return <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Leased</span>;
	};

	/**
	 * Renders notes icon next to guest name (mobile layout only).
	 * All tours are the user's own, so the icon is always interactive.
	 * - Notes exist: "View notes" tooltip, click opens TourNotesModal for editing
	 * - No notes: "Add notes" tooltip, click opens TourNotesModal
	 */
	const renderNotesIcon = (tour) => {
		const tourPropertySlug = tour.property.property_slug;

		if (tour.agent_notes) {
			return (
				<Tooltip text="View notes" clickPassthrough>
					<button
						onClick={() =>
							window.openTourNotesModal({
								tourPublicId: tour.public_id,
								propertySlug: tourPropertySlug,
							})
						}
						className="ml-1.5 shrink-0"
					>
						<PencilSquareIcon className="h-4 w-4 text-purple hover:text-purple-darker transition-colors" />
					</button>
				</Tooltip>
			);
		}

		// No notes — show dimmed icon to invite adding
		return (
			<Tooltip text="Add notes" clickPassthrough>
				<button
					onClick={() =>
						window.openTourNotesModal({
							tourPublicId: tour.public_id,
							propertySlug: tourPropertySlug,
						})
					}
					className="ml-1.5 shrink-0"
				>
					<PencilSquareOutlineIcon className="h-4 w-4 text-gray-600 hover:text-purple transition-colors" />
				</button>
			</Tooltip>
		);
	};

	/**
	 * Renders notes button for the desktop Actions column (teal circular button).
	 * All tours are the user's own, so the button is always interactive.
	 */
	const renderNotesAction = (tour) => {
		const tourPropertySlug = tour.property.property_slug;

		if (tour.agent_notes) {
			return (
				<Tooltip text="View notes" clickPassthrough>
					<button
						onClick={() =>
							window.openTourNotesModal({
								tourPublicId: tour.public_id,
								propertySlug: tourPropertySlug,
							})
						}
						className="flex items-center justify-center w-8 h-8 bg-teal-100 hover:bg-teal-200 text-teal-600 hover:text-teal-700 rounded-full transition-colors"
					>
						<PencilSquareIcon className="h-4 w-4" />
					</button>
				</Tooltip>
			);
		}

		return (
			<Tooltip text="Add notes" clickPassthrough>
				<button
					onClick={() =>
						window.openTourNotesModal({
							tourPublicId: tour.public_id,
							propertySlug: tourPropertySlug,
						})
					}
					className="flex items-center justify-center w-8 h-8 bg-teal-100 hover:bg-teal-200 text-teal-600 hover:text-teal-700 rounded-full transition-colors"
				>
					<PencilSquareOutlineIcon className="h-4 w-4" />
				</button>
			</Tooltip>
		);
	};

	return (
		<div className="overflow-x-auto">
			{filteredTours?.length === 0 ? (
				<div className="flex flex-col items-center justify-center py-8 sm:py-16 text-gray-500">
					<CalendarDaysOutlineIcon className="h-12 w-12 mb-4 text-gray-300" />
					<p className="text-lg font-medium mb-1">
						{activeTab === "scheduled" ? "No upcoming tours" : "No completed tours found"}
					</p>
					<p className="text-sm text-center">
						{tours?.data?.length === 0
							? activeTab === "scheduled"
								? "Booked tours will appear here."
								: "No tours have been completed in the last 90 days."
							: "No tours match your current filters."}
					</p>
				</div>
			) : (
				<>
					{/* Mobile View */}
					{/* Property badge replaces agent badge — clean swap, same visual pattern.
					    May revisit layout later if user-scoped tours need a different mobile card structure. */}
					<div className={forceMobileLayout ? "" : "md:hidden"}>
						{filteredTours?.map((tour, index) => {
							const rowClass = index % 2 === 0 ? "bg-gray-100" : "bg-white";
							const detailParts = [
								tour.preferences?.unit_type,
								tour.preferences?.move_in_date && `Move-in ${tour.preferences.move_in_date}`,
							].filter(Boolean);

							return (
								<div
									key={tour.public_id}
									className={`${rowClass} px-2 sm:px-4 py-3 border-b border-gray-200 last:border-b-0`}
								>
									{/* Time · Date + Property badge + optional Agent badge */}
									<div className="flex flex-wrap items-center gap-1.5">
										<span className="text-xs sm:text-sm font-medium">
											{tour.scheduled_time} · {tour.scheduled_date}
										</span>
										<span className="text-xs bg-purple/10 text-purple px-2 py-0.5 rounded">{tour.property.name}</span>
										{showAgent && tour.agent && (
											<span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">{tour.agent.name}</span>
										)}
									</div>
									{/* Guest name + attendance badge + action icons */}
									<div className="flex items-center mt-2">
										<span className="text-sm text-gray-800 min-w-0 truncate">{tour.primary_contact.name}</span>
										{activeTab === "completed" && renderAttendanceBadge(tour)}
										<div className="flex items-center gap-2 ml-2 shrink-0">
											{tour.message && (
												<Tooltip text="View guest message" clickPassthrough>
													<button onClick={() => setMessageModal({ isOpen: true, message: tour.message })}>
														<EnvelopeIcon className="h-4 w-4 text-gray-500 hover:text-purple transition-colors" />
													</button>
												</Tooltip>
											)}
											{renderNotesIcon(tour)}
											{activeTab === "scheduled" && canCancelTour(tour) && (
												<>
													<Tooltip text="Reschedule Tour" clickPassthrough>
														<button
															onClick={() =>
																window.openRescheduleTourModal({
																	tourPublicId: tour.public_id,
																	propertySlug: tour.property.property_slug,
																})
															}
														>
															<CalendarDaysIcon className="h-4 w-4 text-gray-500 hover:text-purple transition-colors" />
														</button>
													</Tooltip>
													<Tooltip text="Cancel Tour" clickPassthrough>
														<button onClick={() => onCancel(tour)}>
															<TrashIcon className="h-4 w-4 text-gray-500 hover:text-red-500 transition-colors" />
														</button>
													</Tooltip>
												</>
											)}
										</div>
									</div>
									{/* Email and phone */}
									<div className="mt-0.5 text-xs space-y-0.5">
										<div>
											<a href={`mailto:${tour.primary_contact.email}`} className="text-purple hover:text-purple-darker">
												{tour.primary_contact.email}
											</a>
										</div>
										{tour.primary_contact.phone && (
											<div>
												<a href={`tel:${tour.primary_contact.phone}`} className="text-purple hover:text-purple-darker">
													{tour.primary_contact.phone}
												</a>
											</div>
										)}
									</div>
									{/* Details (unit type, move-in, lead source, lease outcome) */}
									{(detailParts.length > 0 ||
										tour.lead_source_display_name ||
										(activeTab === "completed" && tour.has_leased)) && (
										<div className="flex items-center gap-1.5 flex-wrap text-xs text-gray-600 mt-0.5">
											{detailParts.length > 0 && <span>{detailParts.join(" · ")}</span>}
											{tour.lead_source_display_name && (
												<span className="bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
													{tour.lead_source_display_name}
												</span>
											)}
											{activeTab === "completed" && renderLeasedPill(tour)}
										</div>
									)}
								</div>
							);
						})}
					</div>

					{/* Desktop View */}
					<table className={`min-w-full table-auto ${forceMobileLayout ? "hidden" : "hidden md:table"}`}>
						<thead>
							<tr>
								<th className="text-left text-base px-4 py-4 font-semibold">Property</th>
								{showAgent && <th className="text-left text-base px-4 py-4 font-semibold">Agent</th>}
								<th className="text-left text-base px-4 py-4 font-semibold">Time</th>
								<th className="text-left text-base px-4 py-4 font-semibold">Guest</th>
								<th className="text-left text-base px-4 py-4 font-semibold">Details</th>
								<th className="text-left text-base px-4 py-4 font-semibold">Actions</th>
							</tr>
						</thead>
						<tbody>
							{filteredTours?.map((tour, index) => (
								<tr key={tour.public_id} className={index % 2 === 0 ? "bg-gray-100" : "bg-white"}>
									<td className="px-4 py-4 text-sm">
										<div>
											<div>{tour.property.name}</div>
										</div>
									</td>
									{showAgent && (
										<td className="px-4 py-4 text-sm">
											{tour.agent?.name || <span className="text-gray-400">Unassigned</span>}
										</td>
									)}
									{/* whitespace-nowrap prevents date strings like "Feb 16, 2026" from wrapping mid-text at narrow column widths */}
									<td className="px-4 py-4 text-sm whitespace-nowrap">
										<div>{tour.scheduled_time}</div>
										<div className="mt-0.5">{tour.scheduled_date}</div>
									</td>
									<td className="px-4 py-4 text-sm">
										<div className="flex items-center">
											<span className="whitespace-nowrap">{tour.primary_contact.name}</span>
											{activeTab === "completed" && renderAttendanceBadge(tour)}
											{tour.message && (
												<Tooltip text="View guest message" clickPassthrough>
													<button
														onClick={() => setMessageModal({ isOpen: true, message: tour.message })}
														className="ml-2 text-gray-400 hover:text-gray-600"
													>
														<EnvelopeIcon className="h-4 w-4 text-gray-500 hover:text-purple transition-colors" />
													</button>
												</Tooltip>
											)}
										</div>
										<div className="mt-0.5">
											<a
												href={`mailto:${tour.primary_contact.email}`}
												className="text-purple hover:text-purple-darker cursor-pointer"
											>
												{formatEmailForTable(tour.primary_contact.email)}
											</a>
										</div>
										{tour.primary_contact.phone && (
											<div className="mt-0.5">
												<a
													href={`tel:${tour.primary_contact.phone}`}
													className="text-purple hover:text-purple-darker cursor-pointer"
												>
													{tour.primary_contact.phone}
												</a>
											</div>
										)}
									</td>
									<td className="px-4 py-4 text-sm">
										{tour.preferences?.unit_type && <div>{tour.preferences.unit_type}</div>}
										{tour.preferences?.move_in_date && (
											<div className="text-xs text-gray-600 mt-0.5">
												{formatMoveInDate(tour.preferences.move_in_date)}
											</div>
										)}
										{(tour.lead_source_display_name || (activeTab === "completed" && tour.has_leased)) && (
											<div className="mt-1 flex items-center gap-1.5 flex-wrap">
												{tour.lead_source_display_name && (
													<span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
														{tour.lead_source_display_name}
													</span>
												)}
												{activeTab === "completed" && renderLeasedPill(tour)}
											</div>
										)}
									</td>
									<td className="px-4 py-4 text-sm">
										<div className="flex space-x-2">
											{renderNotesAction(tour)}
											{activeTab === "scheduled" && canCancelTour(tour) && (
												<>
													<Tooltip text="Reschedule Tour" clickPassthrough>
														<button
															onClick={() =>
																window.openRescheduleTourModal({
																	tourPublicId: tour.public_id,
																	propertySlug: tour.property.property_slug,
																})
															}
															className="flex items-center justify-center w-8 h-8 bg-indigo-100 hover:bg-indigo-200 text-indigo-600 hover:text-indigo-700 rounded-full transition-colors"
														>
															<CalendarDaysIcon className="h-4 w-4" />
														</button>
													</Tooltip>
													<Tooltip text="Cancel Tour" clickPassthrough>
														<button
															onClick={() => onCancel(tour)}
															className="flex items-center justify-center w-8 h-8 bg-red-100 hover:bg-red-200 text-red-600 hover:text-red-700 rounded-full transition-colors"
														>
															<TrashIcon className="h-4 w-4" />
														</button>
													</Tooltip>
												</>
											)}
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</>
			)}

			{/* Guest Message Modal */}
			<TextModal
				isOpen={messageModal.isOpen}
				onClose={() => setMessageModal({ isOpen: false, message: null })}
				message={messageModal.message}
				title="Guest Message"
			/>
		</div>
	);
}
